import {
  useMutation as useTanStackMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import type { DataDomain, ResourceQueryMeta } from '@/api/resource';

export type MutationEffect =
  | { domains: readonly DataDomain[]; volatileResources?: readonly string[]; noDataEffect?: never }
  | { domains?: never; volatileResources: readonly string[]; noDataEffect?: never }
  | { domains?: never; volatileResources?: never; noDataEffect: true };

declare const undeclaredVariables: unique symbol;

/**
 * What `TVariables` resolves to when nothing declares it: a `mutationFn` with no
 * annotated parameter and no explicit type argument. TanStack's own default is
 * `void`, which let a mutation silently drop the argument every caller passed
 * (`mutate(payload)` against a `() => …` body compiles under `void`). Here it is
 * a brand nothing can produce, and `useRegisteredMutation` refuses it.
 */
export interface UndeclaredMutationVariables {
  readonly [undeclaredVariables]: true;
}

/**
 * Adds an impossible required key while `TVariables` is undeclared, so the
 * declaration itself fails to compile and the key's name says what to do.
 */
type RequireDeclaredVariables<TVariables> = [TVariables] extends [UndeclaredMutationVariables]
  ? { 'declare the variables type: annotate the mutationFn parameter, or (_: void) when there is none': never }
  : unknown;

export type RegisteredMutationOptions<
  TData = unknown,
  TError = Error,
  TVariables = UndeclaredMutationVariables,
  TContext = unknown,
> = UseMutationOptions<TData, TError, TVariables, TContext> & MutationEffect;

type RevisionRefresh = (domains: readonly DataDomain[]) => Promise<void> | void;

let refreshRevisionsAfterMutation: RevisionRefresh = () => {};

/** Installed once by the revision coordinator without coupling hooks to chrome. */
export function setMutationRevisionRefresh(refresh: RevisionRefresh): void {
  refreshRevisionsAfterMutation = refresh;
}

/**
 * The only mutation hook used by the app.
 *
 * Each declaration must name its authoritative domains, its exact volatile
 * resources, or explicitly state that it has no server-data effect. Existing
 * local `onSuccess` work (optimistic cache writes and entity-specific sibling
 * removal) remains valid; this wrapper adds the cross-screen invalidation that
 * those local callbacks cannot safely enumerate.
 */
export function useRegisteredMutation<
  TData = unknown,
  TError = Error,
  TVariables = UndeclaredMutationVariables,
  TContext = unknown,
>(
  options: RegisteredMutationOptions<TData, TError, TVariables, TContext>
    & NoInfer<RequireDeclaredVariables<TVariables>>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const queryClient = useQueryClient();
  const {
    domains,
    volatileResources,
    noDataEffect: _noDataEffect,
    onSuccess,
    ...mutationOptions
  } = options;

  return useTanStackMutation<TData, TError, TVariables, TContext>({
    ...mutationOptions,
    onSuccess: async (...args) => {
      await onSuccess?.(...args);
      if (volatileResources?.length) {
        invalidateNamedVolatileResources(queryClient, volatileResources);
      }
      if (domains?.length) {
        // Refresh/invalidation failure is reported by the global coordinator;
        // it must not turn an already committed mutation into a false failure.
        void Promise.resolve(refreshRevisionsAfterMutation(domains)).catch(() => {});
      }
    },
  });
}

/**
 * Only a volatile read answers to a name here. A domain-backed read is
 * refreshed by the revision coordinator and by nothing else, so a name that
 * points at one — or at nothing — is a silent no-op by construction. That is
 * why `mutation-usage.guard.test.ts` resolves every `volatileResources`
 * literal against the registry: six alert mutations and the brief regenerate
 * named resources this predicate could never match, and no test noticed.
 */
export function invalidateNamedVolatileResources(
  queryClient: QueryClient,
  names: readonly string[],
): void {
  const selected = new Set(names);
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const meta = query.meta as ResourceQueryMeta | undefined;
      return meta?.coherency != null
        && 'volatile' in meta.coherency
        && meta.coherency.volatile === true
        && typeof query.queryKey[1] === 'string'
        && selected.has(query.queryKey[1]);
    },
  });
}
